import { cn } from "@punchup/utils/cn";

/** Structural rules for the body vocabulary (links, lists, blockquote, headings)
 *  — always applied regardless of surface. Typography (size/color/weight) is the
 *  caller's `className` so each surface keeps its own look. Headings are styled
 *  at h4–h6: the sanitizer demotes the editor's stored h2/h3/h4 two levels
 *  (`sanitize-body-html.ts`'s `DEMOTED_HEADING_TAG`) so a user heading nests
 *  below the section's own h2/h3 title, sized at or under it. */
const BODY_STRUCTURE =
  "whitespace-pre-line [&_a]:underline [&_a]:underline-offset-2 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_h4]:text-base [&_h4]:font-semibold [&_h4]:mb-2 [&_h5]:text-sm [&_h5]:font-semibold [&_h5]:mb-2 [&_h6]:text-sm [&_h6]:font-semibold [&_h6]:mb-2";

/** The default base typography — the pre-PUN-5289 `/map` parking paragraph, so
 *  a legacy plain-text row on the venue page renders unchanged. The `/e`
 *  show-description surface overrides it via `className`. */
const DEFAULT_TYPOGRAPHY =
  "text-sm text-secondary-blue leading-relaxed font-light";

/**
 * The presentation half of rich-text body rendering — the prose `<div>`,
 * nothing that pulls cheerio. It takes **already-sanitized** body-vocabulary
 * HTML, so it is safe in BOTH a Server Component (`VenueRichTextBody` sanitizes
 * then renders this) and a client component (a server parent sanitizes with
 * `sanitizeBodyHtml` and passes the string down — the show-description `/e`
 * page). Keep sanitization at a server boundary; this component never does it.
 * New-tab links (`target`/`rel`) are the sanitizer's job via its
 * `externalLinkTarget` option, not this component's.
 *
 * `className` sets the base typography (size/color/weight/line-height); it
 * defaults to the legacy `/map` parking style so existing venue rows don't
 * shift. `whitespace-pre-line` (in the structural rules) is a backstop:
 * `seedBodyHtml` gives a pure plain-text row real `<p>`/`<br>` structure, but a
 * row mixing markup with raw newlines passes through unseeded, and pre-line
 * keeps those breaks visible.
 */
export function RichTextContent({
  html,
  className = DEFAULT_TYPOGRAPHY,
}: {
  html: string;
  className?: string;
}) {
  if (!html) return null;

  return (
    <div
      className={cn(BODY_STRUCTURE, className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
